import { Exception, PageView, RemoteDependencyData } from "@microsoft/applicationinsights-web";
import { appState } from "../AppState";
import { BaseTelemetryPlugin, IProcessTelemetryContext, ITelemetryItem } from "@microsoft/applicationinsights-core-js"
import { CtxTagKeys, PageViewPerformance } from "@microsoft/applicationinsights-common";
import { trimFragment, trimFragments } from "./TrimmingHelpers";

/**
 * Adds application state information and trims URL fragments from telemetry
 */
export class AppStateTelemetryPlugin extends BaseTelemetryPlugin {
    constructor(public RunCorrelationID: string) {
        super();
    }

    /**
     * Processes a telemetry event by adding common properties and trimming URLs
     *
     * @param telemetryItem The current event being processed
     * @param itemCtx This is the context for the current request
     */
    public processTelemetry(telemetryItem: ITelemetryItem, itemCtx?: IProcessTelemetryContext): void {
        // Set common telemetry properties; these MUST be defined first in the telemetry JSON file.
        telemetryItem.data = telemetryItem.data || {};
        telemetryItem.data.Application = "Microsoft Dynamics Office Web Addin";
        telemetryItem.data.CorrelationID = appState.State.CorrelationID;
        telemetryItem.data.RunCorrelationID = this.RunCorrelationID;
        telemetryItem.data.Language = appState.State.Language || "";
        telemetryItem.data.OfficeVersion = appState.State.OfficeVersion;
        telemetryItem.data.CurrentApplication = appState.State.getCurrentApplicationString();
        telemetryItem.data.ClientType = appState.State.getClientTypeString();
        telemetryItem.data.Host = appState.State.Settings.HostName;

        // Add the app version and build type if it was not logged on the event
        if (!(telemetryItem.tags[CtxTagKeys.applicationVersion])) {
            telemetryItem.tags[CtxTagKeys.applicationVersion] = window.dynamicsOfficeBuild.buildVersion;
            telemetryItem.tags[CtxTagKeys.applicationBuild] = window.dynamicsOfficeBuild.buildType;
        }

        // Scrub any 'url' properties
        trimFragment(telemetryItem.data, "url");
        trimFragment(telemetryItem.data, "URLLocation");
        trimFragment(telemetryItem, "url");

        // Only allow the AAD user ID to be logged on page views
        if (telemetryItem.name !== PageView.envelopeType && telemetryItem.name !== PageViewPerformance.envelopeType) {
            if (telemetryItem?.ext?.user) {
                delete telemetryItem.ext.user.authId;
            }
            delete telemetryItem.tags[CtxTagKeys.userAccountId];
        } else {
            // Page views always are tagged with the AAD object and tenant ID
            // Copy the properties from the data object onto the common schema locations so they are ingested correctly
            // This is normally done by setting the user context on the AppInsights instance, but that would result in EUPI on every event
            if (telemetryItem.data.UserID) {
                telemetryItem.ext.user.authId = telemetryItem.data.UserID;
            }
            if (telemetryItem.data.TenantID) {
                telemetryItem.tags[CtxTagKeys.userAccountId] = telemetryItem.data.TenantID;
            }
        }

        // If the telemetry being logged in an exception or dependency data, ensure URL fragments are scrubbed
        if (telemetryItem.name === Exception.envelopeType) {
            trimFragments(telemetryItem);
        } else if (telemetryItem.name === RemoteDependencyData.envelopeType) {
            trimFragments(telemetryItem);
        }

        // Continue processing the event
        this.processNext(telemetryItem, itemCtx);
    }
}